/**
 * Every NUMBER interpolated into a sentence is written in the reader's
 * language (uiv2 7c): "1,725 days", "1 725 jours". Before, i18next printed
 * `{{count}}` bare — "Averaged over 1725 days" beside a table that grouped the
 * same figure as "1,725" (release-traps, the raw-beside-interpolated entry).
 *
 * An i18next formatter module with `alwaysFormat`, so no placeholder in
 * translations.json5 has to opt in (1,900+ do not) and a new one cannot
 * forget to. Strings pass through untouched: a caller that already formatted
 * a figure (`fmtMoney`, `fmtPts`) is not formatted twice. Named formats
 * (`{{x, number}}`) are not used anywhere and are not supported.
 *
 * A number that is an IDENTIFIER, not a quantity, is not grouped: a year
 * ("2,026" is wrong in every language), an invoice or cheque number, an id, a
 * code. Recognised by the placeholder's name, which translations.json5 owns —
 * `number-formatter.test.ts` lists every placeholder that reaches here with a
 * number and pins which side of this line it is on.
 */
import type { FormatterModule } from 'i18next';
import { formatLocale, ltrFigure } from '@/lib/locale';

export const IDENTIFIER_PLACEHOLDER = /^(year|[a-z]+Year|number|invoiceNumber|id|code)$/;

const cache = new Map<string, Intl.NumberFormat>();

function numberFormat(tag: string): Intl.NumberFormat {
  let nf = cache.get(tag);
  if (!nf) {
    // 20 fraction digits: grouping is the change, rounding is not — a figure
    // interpolated as 9.975 stays 9.975. Precision belongs to the caller.
    nf = new Intl.NumberFormat(tag, { maximumFractionDigits: 20 });
    cache.set(tag, nf);
  }
  return nf;
}

/** One interpolated value, as i18next hands it over (`interpolationkey` is the placeholder). */
export function formatInterpolated(value: unknown, lng: string | undefined, key: string | undefined): unknown {
  // `Number.isFinite` is false for anything that is not a number.
  if (!Number.isFinite(value) || IDENTIFIER_PLACEHOLDER.test(key ?? '')) return value;
  const n = value as number;
  const text = numberFormat(formatLocale(lng)).format(Math.abs(n));
  // A negative figure keeps its real minus beside the digits in RTL text.
  return n < 0 ? ltrFigure(`−${text}`, lng) : text;
}

export const numberFormatter: FormatterModule = {
  type: 'formatter',
  init: () => {},
  add: () => {},
  addCached: () => {},
  format: (value, format, lng, options) =>
    format ? value : (formatInterpolated(value, lng, options?.interpolationkey as string | undefined) as string),
};
